import { Add, BorderColor, CancelOutlined, DeleteOutlined, Search } from "@material-ui/icons";
import { observer } from "mobx-react-lite";
import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useAsync, useAsyncFn, useDebounce } from "react-use";
import styled from "styled-components";
import api from "../../api";
import { APISearchParams } from "../../api/base";
import * as projectsAPI from "../../api/projects";
import { APIRCodeSearchResult } from "../../api/types/projects";
import Button from "../../components/Button";
import Loading from "../../components/Loading";
import history from "../../services/history";
import { useStore } from "../../store";
import ProjectFolder from "../../store/models/ProjectFolders";
import { isRCodePattern, Toast } from "../../utils";
import { confirmDeleteFolder } from "../../utils/confirm";
import { Page } from "../Page";
import FolderForm from "./FolderForm";
import NewProject from "./NewProject";
import Projects from "./Projects";
import RCodeSearchResults from "./RCodeSearchResults";

const RootContainer = styled.div`
  margin: 1.875rem 3.75rem;
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.25rem;
`;

const SearchWrapper = styled.div`
  display: flex;
  align-items: stretch;
  border: 1px solid #b9b9b9;
  border-radius: 0.25rem;
  overflow: hidden;
  height: 38px;
  width: fit-content;

  &:focus-within {
    border-color: #4caf50;
    box-shadow: 0 0 0 2px rgba(76, 175, 80, 0.2);
  }
`;

const SearchInput = styled.input`
  padding: 0.4rem 0.75rem;
  border: none;
  font-size: 0.875rem;
  outline: none;
  min-width: 280px;
  background: #ffffff;
`;

const SearchActionButton = styled.button`
  background: transparent;
  border: none;
  border-left: 1px solid #b9b9b9;
  color: #666;
  padding: 0 0.6rem;
  cursor: pointer;
  display: flex;
  align-items: center;

  &:hover {
    background: #f5f5f5;
    color: #333;
  }
`;

const SearchSubmitButton = styled(SearchActionButton)`
  background: #4caf50;
  border-left: 1px solid #43a047;
  color: #ffffff;

  &:hover {
    background: #43a047;
    color: #ffffff;
  }
`;

const OptionContent = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-wrap: wrap;
  gap: 1.25rem;

  margin: 1.875rem 0;
`;

interface FolderProps {}

const Folder: React.FC<FolderProps> = () => {
  const rCodePageSize = 20;
  const { slug } = useParams<{ slug: string }>();
  const store = useStore();
  const currentUser = store.auth.current;
  const urlPrefix = "/projects/";
  const [renameFolderModalShow, setRenameFolderModalShow] =
    useState<boolean>(false);
  const [newProjectModalShow, setNewProjectModalShow] =
    useState<boolean>(false);

  const [inputValue, setInputValue] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [rCodePage, setRCodePage] = useState<number>(1);
  const [rCodeSearchResult, setRCodeSearchResult] = useState<APIRCodeSearchResult | null>(null);
  const [rCodeSearchError, setRCodeSearchError] = useState<string | null>(null);
  const rCodeRequestId = useRef(0);
  const rCodeAbortController = useRef<AbortController | null>(null);
  const isRCodeSearch = Boolean(searchQuery && isRCodePattern(searchQuery));

  // Debounce raw keystrokes before they trigger a search request.
  useDebounce(
    () => {
      const nextQuery = inputValue.trim();
      setSearchQuery((currentQuery) =>
        currentQuery === nextQuery ? currentQuery : nextQuery
      );
    },
    300,
    [inputValue]
  );

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      setRCodePage(1);
      setSearchQuery(inputValue);
    }
  };

  const handleSearchSubmit = () => {
    setRCodePage(1);
    setSearchQuery(inputValue);
  };

  const handleClearSearch = () => {
    setInputValue("");
    setSearchQuery("");
    setRCodeSearchResult(null);
    setRCodeSearchError(null);
    setRCodePage(1);
  };

  // Search the same R-Code endpoint, constrained by the current folder slug.
  const [{ loading: rCodeLoading }, performRCodeSearch] = useAsyncFn(
    async (rcode: string, page = 1) => {
      rCodeAbortController.current?.abort();
      const controller = new AbortController();
      rCodeAbortController.current = controller;
      const requestId = ++rCodeRequestId.current;
      setRCodeSearchError(null);
      try {
        const result = await projectsAPI.searchByRCode(
          rcode,
          page,
          rCodePageSize,
          { folder: slug },
          controller.signal
        );
        if (requestId === rCodeRequestId.current && !controller.signal.aborted) {
          setRCodeSearchResult(result);
        }
      } catch (error: any) {
        if (requestId === rCodeRequestId.current && !controller.signal.aborted) {
          setRCodeSearchError(error?.response?.data?.error || "Failed to search R-Code");
          setRCodeSearchResult(null);
        }
      }
    },
    [slug]
  );

  useEffect(() => {
    if (searchQuery && isRCodePattern(searchQuery)) {
      performRCodeSearch(searchQuery, rCodePage);
    } else {
      rCodeAbortController.current?.abort();
      rCodeRequestId.current += 1;
      setRCodeSearchResult(null);
      setRCodeSearchError(null);
    }
  }, [searchQuery, rCodePage, performRCodeSearch]);

  useEffect(() => () => rCodeAbortController.current?.abort(), []);

  const folder = store.projectFolders.listItems.get(slug);

  const fetchProjectsList = async (params: APISearchParams) => {
    return store.projects.listFolder(slug, undefined, undefined, params);
  };

  const [{ loading: projectsLoading, value: projects }, fetchProjects] =
    useAsyncFn(async () => {
      const params: APISearchParams = {
        // limit: 0,
      };

      const { count } = await fetchProjectsList(params);
      return {
        count,
        results: Array.from(store.projects.listFolderItems.values()),
      };
    });

  useAsync(async () => {
    await store.projectFolders.detail(slug);
  }, []);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const handleProjectSubmit = async () => {
    await fetchProjects();
  };

  const handleFolderDelete = async () => {
    if (folder) {
      if (await confirmDeleteFolder(folder.name)) {
        await api.projectFolders.destroy(folder.slug);
        history.push(urlPrefix);
        Toast.success("Folder Deleted");
      }
    }
  };

  return (
    <Page
      title={folder?.name}
      breadcrumb={[
        { title: "Projects Drive", subtitle: "Projects", link: "/projects" },
        {
          title: folder?.name || "",
          subtitle: `${folder?.isActive ? "Active" : "Archive"} Folder`,
        },
      ]}
    >
      <RootContainer>
        <TopBar>
          {folder?.isActive && (
            <Button
              variant="green"
              size="page"
              onClick={() => setNewProjectModalShow(true)}
            >
              <Add />
              New Project
            </Button>
          )}
          <SearchWrapper>
            <SearchInput
              type="text"
              placeholder="Search by project name or R-Code"
              value={inputValue}
              onChange={(e) => {
                setRCodePage(1);
                setInputValue(e.target.value);
              }}
              onKeyDown={handleSearchKeyDown}
            />
            {inputValue && (
              <SearchActionButton type="button" onClick={handleClearSearch}>
                <CancelOutlined style={{ fontSize: "1rem" }} />
              </SearchActionButton>
            )}
            <SearchSubmitButton type="button" onClick={handleSearchSubmit}>
              <Search style={{ fontSize: "1rem" }} />
            </SearchSubmitButton>
          </SearchWrapper>
        </TopBar>
        {store.auth.isRbacAdmin && (
          <OptionContent>
            <Button
              onClick={() => {
                setRenameFolderModalShow(true);
              }}
            >
              <BorderColor />
              Rename Folder
            </Button>
            <Button onClick={handleFolderDelete}>
              <DeleteOutlined />
              Delete Folder
            </Button>
          </OptionContent>
        )}
        {projectsLoading && !isRCodeSearch ? (
          <Loading />
        ) : isRCodeSearch ? (
          <RCodeSearchResults
            rcode={searchQuery}
            result={rCodeSearchResult}
            loading={rCodeLoading}
            error={rCodeSearchError}
            urlPrefix={urlPrefix}
            page={rCodePage}
            pageSize={rCodePageSize}
            onPageChange={setRCodePage}
            onProjectSubmit={() => performRCodeSearch(searchQuery, rCodePage)}
            onArchiveRestore={() => performRCodeSearch(searchQuery, rCodePage)}
            folderArchived={!folder?.isActive}
          />
        ) : (
          <Projects
            isFolder
            archived={!folder?.isActive}
            title="Projects"
            urlPrefix={urlPrefix}
            projects={projects?.results}
            projectCount={projects?.count}
            searchQuery={searchQuery}
            onProjectSubmit={handleProjectSubmit}
            onArchiveRestore={handleProjectSubmit}
          />
        )}
      </RootContainer>
      <FolderForm
        isInsideFolder
        instance={folder as ProjectFolder}
        archived={!folder?.isActive}
        isOpen={renameFolderModalShow}
        setModal={(visible: boolean) => setRenameFolderModalShow(visible)}
      />
      <NewProject
        slug={slug}
        isOpen={newProjectModalShow}
        setModal={setNewProjectModalShow}
      />
    </Page>
  );
};

export default observer(Folder);
